import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, CircularProgress, Grid } from "@mui/material";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import FormFilePickerField from "../../Form/controls/FilePicker/FormFilePickerField.jsx";
import { useAttachmentConfig } from "../../Form/controls/FilePicker/useAttachmentConfig.js";
import FormTextField from "../../Form/controls/FormTextField.jsx";
import FormSelect from "../../Form/controls/FormSelect.jsx";
import FormDatePicker from "../../Form/controls/FormDatePicker.jsx";
import FormNumberField from "../../Form/controls/FormNumberField.jsx";
import FormSwitch from "../../Form/controls/FormSwitch.jsx";
import DSButton from "@accredo/fe-shared/theme/components/Button.jsx";

import {
  costTypeOptions,
  costEntrySchema,
  getAmountLabel,
  getAttachmentFields,
} from "./costFieldSchemas.js";
import { useClaimCostTypes } from "../../../hooks/useClaimCostTypes.js";

/**
 * Form for creating or editing a single cost entry.
 * Dynamically renders fields based on the selected cost type.
 */
const CostEntryForm = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  sessionAttachmentFiles,
  claimAttachmentFiles,
  updateSessionAttachmentFileDescription,
  addSessionAttachmentFile,
  costTypes,
}) => {
  const costAttachmentAccept = useAttachmentConfig("cost");
  const {
    config: costTypeConfig,
    isError: costTypesFailed,
    reload: reloadCostTypes,
  } = useClaimCostTypes();

  // Helper to parse date strings to dayjs objects (required by MUI DatePicker)
  const parseDate = (dateValue) => {
    if (!dateValue) {
      return null;
    }
    const parsed = dayjs(dateValue);
    return parsed.isValid() ? parsed : null;
  };

  // Transform persisted cost data into the shape the picker expects.
  //
  // Backend stores picker attachment fields as { id: X } (legacy shape kept
  // for backwards-compat with older rows). FormFilePickerField's value is a
  // plain id (string|number) or array of ids, so we normalise here on hydrate.
  // assignment_attachment is also included since it now uses FormFilePickerField.
  const transformDataForForm = (data, costType) => {
    if (!data) {
      return {};
    }
    const pickerFields = new Set([
      ...getAttachmentFields(costTypeConfig, costType),
      "assignment_attachment",
    ]);
    const transformed = { ...data };
    pickerFields.forEach((key) => {
      const value = transformed[key];
      if (value && typeof value === "object" && "id" in value) {
        // transformed[key] = value.id;
      }
    });
    return transformed;
  };

  const getDefaultValues = () => ({
    cost_type: initialData?.cost_type || "",
    amount: initialData?.amount ?? null,
    company_name: initialData?.company_name || "",
    invoice_number: initialData?.invoice_number || "",
    invoice_date: parseDate(initialData?.invoice_date),
    data: transformDataForForm(initialData?.data, initialData?.cost_type),
    is_assigned: initialData?.is_assigned ?? false,
  });

  const methods = useForm({
    defaultValues: getDefaultValues(),
    resolver: yupResolver(costEntrySchema),
  });

  const { handleSubmit, watch, reset } = methods;
  const selectedType = watch("cost_type");
  const isAssigned = watch("is_assigned");

  useEffect(() => {
    reset(getDefaultValues());
  }, [initialData, reset]);

  const handleButtonClick = async () => {
    // Manually trigger validation and submission without using a form element
    // This avoids nested form issues with the parent FormRenderer
    await handleSubmit(async (formData) => {
      // Format date for API (dayjs uses format(), not toISOString())
      const payload = {
        ...formData,
        invoice_date: formData.invoice_date
          ? dayjs(formData.invoice_date).format("YYYY-MM-DD")
          : null,
      };

      // Format data field values (dates)
      if (payload.data) {
        Object.keys(payload.data).forEach((key) => {
          const value = payload.data[key];
          if (dayjs.isDayjs(value)) {
            payload.data[key] = value.format("YYYY-MM-DD");
          }
        });
      }

      await onSubmit(payload);

      // Reset form to empty state after successful submission
      reset({
        cost_type: "",
        amount: null,
        company_name: "",
        invoice_number: "",
        invoice_date: null,
        data: {},
        is_assigned: false,
      });
    })();
  };

  const typeOptions = costTypeOptions(
    costTypeConfig,
    costTypes,
    initialData?.cost_type
  );
  const config = costTypeConfig?.find((type) => type.value === selectedType);
  const showFields = config?.show_fields || [];
  const dataFields = config?.data_fields || [];
  const uploadFields = config?.upload_fields || [];
  const preCommonDataFields = dataFields
    .filter((field) => (field?.order ?? 0) < 0)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  const postCommonDataFields = dataFields
    .filter((field) => (field?.order ?? 0) >= 0)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  const renderDataField = (field) => {
    const name = `data.${field.name}`;

    switch (field.type) {
      case "number":
        return (
          <FormNumberField
            key={field.name}
            name={name}
            label={field.label}
            unit={field.unit}
            fullWidth
          />
        );
      case "date":
        return (
          <FormDatePicker key={field.name} name={name} label={field.label} />
        );
      case "textarea":
        return (
          <FormTextField
            key={field.name}
            name={name}
            label={field.label}
            multiline
            rows={3}
            fullWidth
          />
        );
      default:
        return (
          <FormTextField
            key={field.name}
            name={name}
            label={field.label}
            fullWidth
          />
        );
    }
  };

  if (!costTypeConfig) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        {costTypesFailed ? (
          <DSButton variant="secondary" onClick={reloadCostTypes}>
            Kostenarten konnten nicht geladen werden. Erneut versuchen
          </DSButton>
        ) : (
          <CircularProgress size={24} aria-label="Kostenarten werden geladen" />
        )}
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormSelect
              name="cost_type"
              label="Kostenart"
              options={typeOptions}
            />
          </Grid>

          {selectedType && (
            <>
              {preCommonDataFields.map((field) => (
                <Grid key={field.name} size={{ xs: 12, md: 6 }}>
                  {renderDataField(field)}
                </Grid>
              ))}

              {showFields.includes("company_name") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormTextField
                    name="company_name"
                    label="Name Firma"
                    fullWidth
                  />
                </Grid>
              )}

              {showFields.includes("invoice_number") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormTextField
                    name="invoice_number"
                    label="Rechnungsnummer"
                    fullWidth
                  />
                </Grid>
              )}

              {showFields.includes("invoice_date") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormDatePicker name="invoice_date" label="Rechnungsdatum" />
                </Grid>
              )}

              {showFields.includes("amount") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormNumberField
                    name="amount"
                    label={getAmountLabel(costTypeConfig, selectedType)}
                    unit="€"
                    fullWidth
                  />
                </Grid>
              )}

              {postCommonDataFields.map((field) => (
                <Grid key={field.name} size={{ xs: 12, md: 6 }}>
                  {renderDataField(field)}
                </Grid>
              ))}

              {uploadFields.length > 0 && (
                <Grid size={12}>
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      {uploadFields.map((field) => (
                        <Grid key={field.name} size={{ xs: 12 }}>
                          <FormFilePickerField
                            name={`data.${field.name}`}
                            label={field.label}
                            trackAsSource={true}
                            existingFiles={claimAttachmentFiles}
                            sessionFiles={sessionAttachmentFiles}
                            onAddSessionFile={addSessionAttachmentFile}
                            onUpdateSessionFileDescription={
                              updateSessionAttachmentFileDescription
                            }
                            addDescription={true}
                            accept={costAttachmentAccept ?? []}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              )}

              <Grid size={12}>
                <FormSwitch
                  name="is_assigned"
                  label="Abtretungserklärung (Zahlung direkt vom Versicherer an Dienstleister)"
                />
              </Grid>

              {isAssigned && (
                <Grid size={12}>
                  <FormFilePickerField
                    name="data.assignment_attachment"
                    label="Abtretungserklärung (Dokument)"
                    trackAsSource={true}
                    existingFiles={claimAttachmentFiles}
                    sessionFiles={sessionAttachmentFiles}
                    onAddSessionFile={addSessionAttachmentFile}
                    onUpdateSessionFileDescription={
                      updateSessionAttachmentFileDescription
                    }
                    addDescription={true}
                    accept={costAttachmentAccept}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>

        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <DSButton
            variant="tertiary"
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Abbrechen
          </DSButton>
          <DSButton
            onClick={handleButtonClick}
            size="small"
            disabled={isSubmitting || !selectedType}
            loading={isSubmitting}
          >
            {initialData?.id ? "Übernehmen" : "Hinzufügen"}
          </DSButton>
        </Box>
      </Box>
    </FormProvider>
  );
};

CostEntryForm.propTypes = {
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  sessionAttachmentFiles: PropTypes.array,
  claimAttachmentFiles: PropTypes.array,
  updateSessionAttachmentFileDescription: PropTypes.func,
  addSessionAttachmentFile: PropTypes.func.isRequired,
  costTypes: PropTypes.arrayOf(PropTypes.string),
};

export default CostEntryForm;
